/**
 * Custom Hook for Customers Management
 */

import { useState, useEffect, useCallback } from 'react';
import {
  customersService,
  Customer,
  CustomersQuery,
  CreateCustomerDto,
  UpdateCustomerDto,
  CustomerStats,
} from '../services/customers.service';

interface UseCustomersState {
  customers: Customer[];
  selectedCustomer: Customer | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  stats: CustomerStats | null;
}

export function useCustomers(initialQuery?: CustomersQuery) {
  const [state, setState] = useState<UseCustomersState>({
    customers: [],
    selectedCustomer: null,
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    loading: true,
    error: null,
    stats: null,
  });

  const [query, setQuery] = useState<CustomersQuery>(initialQuery || { page: 1, limit: 50 });

  const fetchCustomers = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await customersService.getCustomers(query);
      if (response.success && response.data) {
        const payload: any = response.data;
        const firstDataLayer = payload?.data;
        const secondDataLayer = firstDataLayer?.data;

        const customers: Customer[] = Array.isArray(payload)
          ? payload
          : Array.isArray(firstDataLayer)
            ? firstDataLayer
            : Array.isArray(secondDataLayer)
              ? secondDataLayer
              : [];

        const meta =
          payload?.meta ||
          firstDataLayer?.meta ||
          secondDataLayer?.meta ||
          {};

        setState((prev) => ({
          ...prev,
          customers,
          total: Number(meta.total ?? payload?.total ?? firstDataLayer?.total ?? customers.length),
          page: Number(meta.page ?? payload?.page ?? firstDataLayer?.page ?? 1),
          limit: Number(meta.limit ?? payload?.limit ?? firstDataLayer?.limit ?? 50),
          totalPages: Number(meta.totalPages ?? payload?.totalPages ?? firstDataLayer?.totalPages ?? 1),
          loading: false,
        }));
      } else {
        setState((prev) => ({ ...prev, loading: false }));
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load customers',
      }));
    }
  }, [query]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await customersService.getCustomerStats();
      if (response.success && response.data) {
        setState((prev) => ({ ...prev, stats: response.data! }));
      }
    } catch (error: any) {
      console.error('Failed to fetch customer stats:', error);
    }
  }, []);

  const fetchCustomer = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await customersService.getCustomerById(id);
      if (response.success && response.data) {
        setState((prev) => ({
          ...prev,
          selectedCustomer: response.data!,
          loading: false,
        }));
        return response.data;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load customer',
      }));
    }
    return null;
  }, []);

  const createCustomer = useCallback(async (data: CreateCustomerDto) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await customersService.createCustomer(data);
      if (response.success) {
        await fetchCustomers();
        return response.data;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to create customer',
      }));
      throw error;
    }
  }, [fetchCustomers]);

  const updateCustomer = useCallback(async (id: string, data: UpdateCustomerDto) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await customersService.updateCustomer(id, data);
      if (response.success) {
        await fetchCustomers();
        return response.data;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to update customer',
      }));
      throw error;
    }
  }, [fetchCustomers]);

  const deleteCustomer = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await customersService.deleteCustomer(id);
      await fetchCustomers();
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to delete customer',
      }));
      throw error;
    }
  }, [fetchCustomers]);

  const updateQuery = useCallback((newQuery: Partial<CustomersQuery>) => {
    setQuery((prev) => ({ ...prev, ...newQuery }));
  }, []);

  const refresh = useCallback(() => {
    fetchCustomers();
    fetchStats();
  }, [fetchCustomers, fetchStats]);

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, [fetchCustomers, fetchStats]);

  return {
    customers: state.customers,
    selectedCustomer: state.selectedCustomer,
    total: state.total,
    page: state.page,
    limit: state.limit,
    totalPages: state.totalPages,
    loading: state.loading,
    error: state.error,
    stats: state.stats,
    query,
    fetchCustomer,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    updateQuery,
    refresh,
  };
}
